import { useEffect, useRef } from "react";

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: Readonly<Record<string, unknown>>,
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-tongxin-turnstile="true"]',
    );
    const script = existing ?? document.createElement("script");
    const ready = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error("Turnstile API unavailable"));
    script.addEventListener("load", ready, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Turnstile script failed to load")),
      { once: true },
    );
    if (!existing) {
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.defer = true;
      script.dataset.tongxinTurnstile = "true";
      document.head.append(script);
    }
  });
  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  resetKey = 0,
  onToken,
  onError,
}: {
  siteKey: string;
  resetKey?: number;
  onToken: (token: string | null) => void;
  onError?: (message: string) => void;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let active = true;
    let widgetId: string | null = null;
    onToken(null);
    void loadTurnstile()
      .then((api) => {
        if (!active || !container.current) return;
        widgetId = api.render(container.current, {
          sitekey: siteKey,
          theme: "dark",
          size: "flexible",
          language: "zh-CN",
          action: "request_otp",
          callback: (token: string) => {
            if (active) onToken(token);
          },
          "expired-callback": () => {
            if (active) onToken(null);
          },
          "timeout-callback": () => {
            if (active) onToken(null);
          },
          "error-callback": () => {
            if (active) {
              onToken(null);
              onError?.("人机验证暂时不可用，请刷新后重试");
            }
          },
        });
      })
      .catch(() => {
        if (active) onError?.("无法载入人机验证，请检查网络");
      });
    return () => {
      active = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onError, onToken, resetKey, siteKey]);
  return (
    <div
      className="turnstile-widget"
      ref={container}
      role="group"
      aria-label="人机验证"
    />
  );
}
